import { PageHero } from '@components/common';
import { getKnowledgeCard } from '@data/knowledgeCards';
import {
  ArrowRight,
  CheckCircle,
  Users,
  GraduationCap,
  Briefcase,
  Lightbulb,
  TrendingUp,
} from 'lucide-react';

export default function BecomeFractionalCisoPage() {
  return (
    <>
      <PageHero
        title="How to Become a CISO"
        subtitle="A practical guide for security professionals aspiring to chief information security officer roles - and how the fractional path can accelerate your journey."
        eyebrow="Career Guide"
        backgroundImage={getKnowledgeCard('become-a-fractional-ciso')?.image}
        backgroundAlt={getKnowledgeCard('become-a-fractional-ciso')?.imageAlt}
        seoTitle="How to Become a CISO | The CISO Network"
        seoDescription="Learn how to become a CISO. From building the right experience to developing board-level skills, this guide covers the career path to security leadership."
      />

      {/* Introduction */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
              What Does a CISO Do?
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                The Chief Information Security Officer (CISO) is the senior executive responsible
                for an organisation's information and cyber security strategy. It is a role that
                sits at the intersection of technology, risk management, business strategy, and
                people leadership.
              </p>
              <p>
                A CISO's responsibilities typically include setting the security vision, building
                and leading the security team, managing risk and compliance, responding to
                incidents, and communicating with the board. It is a high-accountability role that
                requires both deep technical understanding and strong commercial acumen.
              </p>
              <p>
                Whether you aim to be a full-time CISO in a single organisation or explore the
                growing fractional CISO model, the foundational skills and experience are largely
                the same.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Path */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              The Typical Path to Becoming a CISO
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              There is no single route to the top, but most CISOs follow a similar trajectory.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  step: '01',
                  title: 'Build Deep Technical Foundations',
                  description:
                    'Start in hands-on security roles - SOC analyst, penetration tester, security engineer, or IT risk analyst. This gives you the credibility to lead technical teams and make informed decisions later in your career.',
                },
                {
                  step: '02',
                  title: 'Move into Leadership',
                  description:
                    'Progress into management roles such as Security Manager or Head of Security. Here you learn to build teams, manage budgets, and translate security priorities into business terms.',
                },
                {
                  step: '03',
                  title: 'Develop Strategic and Business Skills',
                  description:
                    'The step from manager to CISO is about breadth. You need to understand corporate strategy, financial planning, legal and regulatory frameworks, and how to influence senior stakeholders.',
                },
                {
                  step: '04',
                  title: 'Gain Board-Level Experience',
                  description:
                    'Before becoming a CISO, most successful candidates have spent time presenting to executives, serving on committees, or leading cross-functional initiatives. Board exposure is often the differentiator.',
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-14 w-14 rounded-xl bg-teal flex items-center justify-center">
                      <span className="text-lg font-bold text-accent-foreground">{item.step}</span>
                    </div>
                  </div>
                  <div className="flex-grow pb-8 border-b border-border last:border-0">
                    <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Skills */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Essential Skills for Any CISO
              </h2>
              <p className="text-muted-foreground mb-8">
                Technical knowledge gets you to the table. These skills get you the seat.
              </p>
              <div className="space-y-4">
                {[
                  'Strategic thinking - aligning security with business objectives',
                  'Leadership and team building - recruiting, developing, and retaining talent',
                  'Risk management - identifying, quantifying, and treating cyber risk',
                  'Communication - presenting complex topics clearly to non-technical audiences',
                  'Stakeholder influence - building trust with executives and the board',
                  'Crisis management - leading calmly under pressure during incidents',
                  'Business acumen - understanding finance, operations, and commercial drivers',
                  'Governance and compliance - navigating regulations and frameworks',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <h3 className="text-xl font-heading font-bold text-foreground mb-6 text-center">
                Common Backgrounds
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-border">
                  <div className="h-10 w-10 rounded-xl bg-teal/10 flex items-center justify-center">
                    <Briefcase className="h-5 w-5 text-teal" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Technical Security Track</p>
                    <p className="text-sm text-muted-foreground">
                      Engineers and analysts who moved into management and strategy
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-border">
                  <div className="h-10 w-10 rounded-xl bg-teal/10 flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-teal" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Risk and Compliance</p>
                    <p className="text-sm text-muted-foreground">
                      Auditors and risk managers who developed deep security expertise
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-border">
                  <div className="h-10 w-10 rounded-xl bg-teal/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-teal" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">IT Leadership</p>
                    <p className="text-sm text-muted-foreground">
                      Infrastructure and operations leaders who specialised in security
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8 text-center">
              Certifications That Help
            </h2>
            <p className="text-muted-foreground text-center mb-12">
              While experience matters most, the right certifications can open doors and validate
              your expertise.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                {
                  title: 'CISSP',
                  description: 'The gold standard for senior security professionals.',
                },
                {
                  title: 'CISM',
                  description: 'Focused on information security management and governance.',
                },
                {
                  title: 'CIPP/E or CIPM',
                  description: 'Valuable for CISOs in regulated or privacy-heavy industries.',
                },
                {
                  title: 'CRISC or CGEIT',
                  description: 'Useful for risk management and IT governance credibility.',
                },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-border">
                  <h3 className="font-heading font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Fractional Section */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6 text-center">
              The Fractional CISO Path
            </h2>
            <div className="space-y-4 text-muted-foreground mb-12">
              <p>
                Once you have established yourself as a senior security leader, the fractional model
                offers an alternative to traditional full-time employment. As a fractional CISO, you
                work with multiple organisations on a part-time basis, providing strategic
                leadership without the day-to-day operational burden of a single employer.
              </p>
              <p>
                This path suits experienced CISOs who want variety, flexibility, and greater control
                over their schedule. It can also be a valuable stepping stone for aspiring CISOs who
                want to build board-level experience across several organisations before taking a
                full-time executive role.
              </p>
            </div>

            <h3 className="text-xl font-heading font-bold text-foreground mb-8 text-center">
              Why Join The CISO Network?
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Briefcase,
                  title: 'Qualified Leads',
                  description:
                    'We bring the clients to you. Focus on delivery, not business development.',
                },
                {
                  icon: TrendingUp,
                  title: 'Competitive Rates',
                  description:
                    'Earn competitive rates depending on your experience and the engagement.',
                },
                {
                  icon: Users,
                  title: 'Peer Support',
                  description:
                    'Access a network of experienced CISOs for collaboration, advice, and knowledge sharing.',
                },
                {
                  icon: ArrowRight,
                  title: 'Flexibility',
                  description:
                    'Choose the engagements that interest you and control your own schedule.',
                },
                {
                  icon: Lightbulb,
                  title: 'Proven Methodology',
                  description:
                    'Use our templates, frameworks, and tooling to deliver value from day one.',
                },
                {
                  icon: CheckCircle,
                  title: 'Operational Support',
                  description:
                    'We handle contracting, cover, and client relationships so you do not have to.',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-background rounded-2xl p-8 border border-border text-center"
                >
                  <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center mx-auto mb-6">
                    <item.icon className="h-6 w-6 text-accent-foreground" />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground mb-3">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <a href="/join" className="btn-hero inline-flex items-center">
                Apply to Join the Network <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Already a Senior Security Leader?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            If you have the experience and are exploring the fractional CISO path, we would love to
            hear from you. Join our network and we will be in touch when the right opportunity comes
            up.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/join" className="btn-hero">
              Join the Network <ArrowRight className="ml-2 h-4 w-4" />
            </a>
            <a href="/careers" className="btn-secondary-custom">
              View All Career Opportunities
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
